import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Users, User, CalendarDays, Wallet, BookOpen, MessageSquareHeart,
  ClipboardList, ShieldCheck, Save, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";

const SECTIONS = [
  { key: "profile",     label: "Profile & Class",  desc: "Child's profile, enrolled class and section, address.",          icon: User,             tint: "bg-[#DBEAFE] text-[#2563EB]" },
  { key: "attendance",  label: "Attendance",       desc: "Daily attendance summary and recent history.",                    icon: CalendarDays,     tint: "bg-[#DCFCE7] text-[#15803D]" },
  { key: "payments",    label: "Payment History",  desc: "Tuition payment ledger and remaining balance.",                   icon: Wallet,           tint: "bg-[#FEF3C7] text-[#92400E]" },
  { key: "grades",      label: "Grades",           desc: "Per-subject scores and assessments.",                             icon: BookOpen,         tint: "bg-[#FAE8FF] text-[#7E22CE]" },
  { key: "feedback",    label: "Teacher Feedback", desc: "Progress feedback notes left by teachers.",                       icon: MessageSquareHeart, tint: "bg-[#FFE4E6] text-[#BE123C]" },
  { key: "report_card", label: "Report Card",      desc: "End-of-period printable report cards.",                           icon: ClipboardList,    tint: "bg-[#E0E7FF] text-[#3730A3]" },
  { key: "guardians",   label: "Family / Guardians", desc: "Names, contact details, and photos of registered guardians.",   icon: Users,            tint: "bg-[#FFE8D6] text-[#9A3412]" },
];

const DEFAULT_VIS = SECTIONS.reduce((a, s) => ({ ...a, [s.key]: true }), {});

export default function ParentPortalSettingsPage() {
  const { selectedSchool, schools, refresh } = useBranch();
  const { user } = useAuth();
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canManage = isOwner || !!perms.parent_portal_manage;
  const [vis, setVis] = useState(DEFAULT_VIS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reset local state whenever the selected school changes.
  useEffect(() => {
    const incoming = (selectedSchool && selectedSchool.parent_visibility) || DEFAULT_VIS;
    setVis({ ...DEFAULT_VIS, ...incoming });
    setDirty(false);
  }, [selectedSchool?.id]);

  const allOn = useMemo(() => SECTIONS.every((s) => vis[s.key]), [vis]);

  const toggle = (key) => {
    if (!canManage) return;
    setVis((v) => ({ ...v, [key]: !v[key] }));
    setDirty(true);
  };

  const setAll = (val) => {
    if (!canManage) return;
    setVis(SECTIONS.reduce((a, s) => ({ ...a, [s.key]: val }), {}));
    setDirty(true);
  };

  const save = async () => {
    if (!selectedSchool || !canManage) return;
    setSaving(true);
    try {
      // Use staff-friendly dedicated endpoint (works for owner + staff with parent_portal_manage).
      await api.patch(`/schools/${selectedSchool.id}/parent-visibility`, { parent_visibility: vis });
      toast.success("Parent portal settings saved.");
      setDirty(false);
      refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="max-w-4xl space-y-6">
        {/* Hero */}
        <Card className="p-6 rounded-3xl border-0 shadow-floating" data-testid="ppx-hero">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#2563EB]" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">Parents login at <span className="text-[#2563EB]">{selectedSchool?.slug ? `/school/${selectedSchool.slug}` : "your school portal"}</span></h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                For each enrolled student, set <span className="font-semibold">"Enable Login"</span> on a guardian's profile. The guardian's first password is <span className="font-semibold">the child's birthday in DDMMYYYY</span> (e.g. <span className="font-mono">25122017</span>). They'll be asked to set a new password right after signing in.
              </p>
            </div>
          </div>
        </Card>

        {/* Visibility */}
        <Card className="p-6 rounded-3xl border-0 shadow-floating" data-testid="ppx-visibility">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-bold">What parents can see</h3>
              <p className="text-sm text-slate-500">Applies to all parents of {selectedSchool?.name || "this school"}.</p>
            </div>
            {canManage && (
              <Button variant="outline" onClick={() => setAll(!allOn)}
                className="rounded-full" data-testid="ppx-toggle-all">
                {allOn ? "Turn all off" : "Turn all on"}
              </Button>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {SECTIONS.map((s) => (
              <label key={s.key}
                className={`flex items-center justify-between gap-4 py-4 ${canManage ? "cursor-pointer" : "cursor-not-allowed opacity-90"}`}
                data-testid={`ppx-row-${s.key}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <span className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${s.tint}`}>
                    <s.icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold">{s.label}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
                <Switch checked={!!vis[s.key]} disabled={!canManage} onCheckedChange={() => toggle(s.key)} data-testid={`ppx-switch-${s.key}`} />
              </label>
            ))}
          </div>
        </Card>

        {/* Save */}
        {canManage && (
          <div className="flex items-center justify-end gap-2 sticky bottom-4">
            {dirty && <span className="text-xs text-slate-500">Unsaved changes</span>}
            <Button onClick={save} disabled={!dirty || saving}
              className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] h-11 px-6 text-white font-heading font-semibold shadow-floating"
              data-testid="ppx-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
